// client/src/pages/admin/AdminCitiesManagement.js
import React, { useState, useEffect } from 'react';
import { cityAPI } from '../../services/api';
import { toast } from 'react-toastify';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal';
import { FiPlus, FiMapPin, FiEye, FiEyeOff, FiEdit, FiTrash2 } from 'react-icons/fi';
import normalizeImagePath from '../../utils/imageUtils';

const AdminCitiesManagement = () => {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCity, setEditingCity] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    nameAr: '',
    region: '',
    order: 0,
    active: true
  });
  // Photo de la ville : fichier a envoyer, apercu, et demande d'effacement.
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [removeImage, setRemoveImage] = useState(false);
  // null = pas encore verifie, false = serveur trop ancien.
  const [serverSupportsImage, setServerSupportsImage] = useState(null);

  useEffect(() => {
    loadCities();
  }, []);

  const loadCities = async () => {
    try {
      setLoading(true);
      const response = await cityAPI.getAll({ active: 'all', limit: 200 });
      const list = response.data.data || [];
      setCities(list);

      // Le serveur connait-il la photo de ville ?
      //
      // Si le lot serveur n'est pas deploye, la route n'a pas multer : le corps
      // multipart n'est meme pas lu, l'enregistrement se fait avec des champs
      // VIDES et l'API repond « succes ». On voyait donc un message vert alors
      // que rien n'avait ete enregistre — le pire des cas. On le detecte ici :
      // la propriete `image` n'existe QUE dans la version livree.
      if (list.length) {
        setServerSupportsImage(Object.prototype.hasOwnProperty.call(list[0], 'image'));
      }
    } catch (error) {
      console.error('Error loading cities:', error);
      toast.error('Erreur lors du chargement des villes');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Multipart SEULEMENT si une photo est en jeu. Envoyer systematiquement
      // un FormData obligerait le serveur a re-parser tous les champs, alors
      // que multer ignore deja une requete JSON — on ne change rien au cas
      // courant.
      if ((imageFile || removeImage) && serverSupportsImage === false) {
        toast.error(
          "Le serveur n'est pas a jour : la photo ne peut pas etre enregistree. "
          + "Deployez le lot v1.448.0 (models-sql/City.js, controllers/cityController.js, "
          + "routes/cityRoutes.js) puis redemarrez.",
          { autoClose: 12000 }
        );
        return;
      }

      let payload = formData;
      let config;
      if (imageFile || removeImage) {
        const fd = new FormData();
        Object.entries(formData).forEach(([k, v]) => fd.append(k, v));
        if (imageFile) fd.append('image', imageFile);
        if (removeImage) fd.append('removeImage', '1');
        payload = fd;
        config = { headers: { 'Content-Type': 'multipart/form-data' } };
      }

      if (editingCity) {
        await cityAPI.update(editingCity.id || editingCity._id, payload, config);
        toast.success('Ville mise à jour avec succès');
      } else {
        await cityAPI.create(payload, config);
        toast.success('Ville créée avec succès');
      }

      setShowModal(false);
      setEditingCity(null);
      setFormData({ name: '', nameAr: '', region: '', order: 0, active: true });
      setImageFile(null); setImagePreview(null); setRemoveImage(false);
      loadCities();
    } catch (error) {
      console.error('Error saving city:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de la sauvegarde');
    }
  };

  const handleEdit = (city) => {
    setEditingCity(city);
    setFormData({
      name: city.name,
      nameAr: city.nameAr || '',
      region: city.region || '',
      order: city.sortOrder ?? city.order ?? 0,
      active: city.isActive ?? city.active ?? true
    });
    setImageFile(null);
    setRemoveImage(false);
    setImagePreview(city.image ? normalizeImagePath(city.image) : null);
    setShowModal(true);
  };

  const handleDelete = async () => {
    try {
      await cityAPI.delete(deleteTarget.id || deleteTarget._id);
      toast.success('Ville supprimée avec succès');
      setDeleteTarget(null);
      loadCities();
    } catch (error) {
      console.error('Error deleting city:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const handleToggle = async (city) => {
    try {
      await cityAPI.toggle(city.id || city._id);
      toast.success(`Ville ${(city.isActive ?? city.active) ? 'désactivée' : 'activée'}`);
      loadCities();
    } catch (error) {
      console.error('Error toggling city:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Gestion des Villes</h1>
        <button
          onClick={() => {
            setEditingCity(null);
            setFormData({ name: '', nameAr: '', region: '', order: 0, active: true });
            setShowModal(true);
          }}
          className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition"
        >
          <FiPlus className="inline-block mr-2" />
          Ajouter une ville
        </button>
      </div>

      {/* Bandeau de diagnostic : il ne s'affiche QUE si le serveur ignore la
          colonne image. Sans lui, l'enregistrement repondait « succes » sans
          rien enregistrer — un faux positif est pire qu'une erreur. */}
      {serverSupportsImage === false && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <b>Le serveur n'est pas à jour.</b> La photo de ville ne sera pas
          enregistrée, et l'API répondra quand même « succès ».
          <br />
          À déployer : <code>models-sql/City.js</code>,{' '}
          <code>controllers/cityController.js</code>,{' '}
          <code>routes/cityRoutes.js</code> (lot v1.448.0), puis redémarrer.
          Si le problème persiste, la colonne manque en base :{' '}
          <code>node scripts/repair-schema.js</code>.
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Ville
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Région
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Ordre
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Statut
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {cities.map((city) => {
              const active = city.isActive ?? city.active ?? true;
              const cid = city.id || city._id;
              const ord = city.sortOrder ?? city.order ?? 0;
              return (
              <tr key={cid} className={!active ? 'bg-gray-50 opacity-60' : ''}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <FiMapPin className="text-primary-600 mr-3" />
                    <div>
                      <div className="text-sm font-medium text-gray-900">{city.name}</div>
                      <div className="text-xs text-gray-500">{city.slug}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  {city.region || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  {ord}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    active 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <button
                    onClick={() => handleToggle(city)}
                    className="text-blue-600 hover:text-blue-900 mr-3"
                    title={active ? 'Désactiver' : 'Activer'}
                  >
                    {active ? <FiEyeOff /> : <FiEye />}
                  </button>
                  <button
                    onClick={() => handleEdit(city)}
                    className="text-indigo-600 hover:text-indigo-900 mr-3"
                    title="Modifier"
                  >
                    <FiEdit />
                  </button>
                  <button
                    onClick={() => setDeleteTarget(city)}
                    className="text-red-600 hover:text-red-900"
                    title="Supprimer"
                  >
                    <FiTrash2 />
                  </button>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>

        {cities.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            Aucune ville trouvée. Créez-en une pour commencer.
          </div>
        )}
      </div>

      {/* Modal Create/Edit */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full">
            <h2 className="text-2xl font-bold mb-6">
              {editingCity ? 'Modifier la ville' : 'Nouvelle ville'}
            </h2>

            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-gray-700 mb-2">Nom de la ville *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full border border-gray-300 rounded px-4 py-2"
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 mb-2">Nom (arabe)</label>
                <input
                  type="text"
                  dir="rtl"
                  value={formData.nameAr}
                  onChange={(e) => setFormData({ ...formData, nameAr: e.target.value })}
                  className="w-full border border-gray-300 rounded px-4 py-2"
                  style={{ fontFamily: 'Cairo, sans-serif' }}
                  placeholder="مثال: الرباط"
                />
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 mb-2">Région</label>
                <input
                  type="text"
                  value={formData.region}
                  onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                  className="w-full border border-gray-300 rounded px-4 py-2"
                  placeholder="Ex: Tanger-Tétouan-Al Hoceïma"
                />
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 mb-2">Photo de la ville</label>
                {imagePreview && (
                  <div className="relative mb-2 w-full h-36 rounded-lg overflow-hidden border">
                    <img src={imagePreview} alt="" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => { setImageFile(null); setImagePreview(null); setRemoveImage(true); }}
                      className="absolute top-2 right-2 px-2 py-1 rounded bg-white/90 text-red-600 text-xs font-semibold border"
                    >
                      Retirer
                    </button>
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const f = e.target.files && e.target.files[0];
                    if (!f) return;
                    // Meme plafond que le serveur (5 Mo). Le verifier ici evite
                    // un televersement complet suivi d'un refus.
                    if (f.size > 5 * 1024 * 1024) {
                      toast.error('Image trop lourde (5 Mo maximum)');
                      e.target.value = '';
                      return;
                    }
                    setImageFile(f);
                    setRemoveImage(false);
                    setImagePreview(URL.createObjectURL(f));
                  }}
                  className="w-full border border-gray-300 rounded px-4 py-2"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Affichée sur la page d'accueil. Format paysage conseillé, 5 Mo maximum.
                  Sans photo, la carte garde l'icône actuelle.
                </p>
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 mb-2">Ordre d'affichage</label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                  className="w-full border border-gray-300 rounded px-4 py-2"
                  min="0"
                />
              </div>

              <div className="mb-6">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-gray-700">Ville active</span>
                </label>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingCity(null);
                  }}
                  className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  {editingCity ? 'Mettre à jour' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteTarget && (
        <ConfirmDeleteModal
          isOpen={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
          title="Supprimer la ville"
          message={`Êtes-vous sûr de vouloir supprimer "${deleteTarget.name}" ?`}
        />
      )}
    </div>
  );
};

export default AdminCitiesManagement;